import { useState } from "react";
import { useMutation } from "@apollo/client";
import { UPDATE_ACCESS_REVIEW_ASSESSMENT } from "../../../../../apis/settingsApis/access-apis/accessApis";
import { ReviewDecisionStatus, AssessmentStatus } from "../../../../../utils/enums";

export const useAssessmentMutations = (
  currentAssessment,
  users,
  decisions,
  commentsByUser,
  refetchAssessments,
  refetchUsersData
) => {
  const [mutateUpdateAssessment] = useMutation(UPDATE_ACCESS_REVIEW_ASSESSMENT, {
    fetchPolicy: "no-cache",
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDiscarding, setIsDiscarding] = useState(false);
  const [snackbarInfo, setSnackbarInfo] = useState(null);

  const submitAssessment = async () => {
    if (!currentAssessment) return;
    
    setIsSubmitting(true);
    try {
      const userUpdates = users.map((user) => {
        const update = {
          user_id: user.id,
          review_status: decisions[user.id] || ReviewDecisionStatus.APPROVED,
        };
        if (commentsByUser[user.id]) {
          update.review_note = commentsByUser[user.id];
        }
        return update;
      });

      const submitResult = await mutateUpdateAssessment({
        variables: {
          input: [
            {
              id: currentAssessment.id,
              status: AssessmentStatus.COMPLETED,
              user_updates: userUpdates,
            },
          ],
        },
      });
      
      const submitResp = submitResult?.data?.updateAccessReviewAssesement;
      if (submitResp?.success) {
        setSnackbarInfo({
          message: submitResp?.message || "Access review completed successfully!",
          vertical: "top",
          horizontal: "right",
        });
        refetchAssessments();
        refetchUsersData();
      } else {
        setSnackbarInfo({
          message: submitResp?.message || "Submit failed",
          vertical: "top",
          horizontal: "right",
          severity: "error",
        });
        setIsSubmitting(false);
      }
    } catch (e) {
      setSnackbarInfo({
        message: e.message || "Submit failed",
        vertical: "top",
        horizontal: "right",
        severity: "error",
      });
      setIsSubmitting(false);
    }
  };

  const discardAssessment = async () => {
    if (!currentAssessment) return;
    
    setIsDiscarding(true);
    try {
      const discardResult = await mutateUpdateAssessment({
        variables: {
          input: [{ id: currentAssessment.id, status: AssessmentStatus.REJECTED }],
        },
      });
      
      const discardResp = discardResult?.data?.updateAccessReviewAssesement;
      if (discardResp?.success) {
        setSnackbarInfo({
          message: discardResp?.message || "Assessment discarded",
          vertical: "top",
          horizontal: "right",
          severity: "info",
        });
        refetchAssessments();
      } else {
        setSnackbarInfo({
          message: discardResp?.message || "Discard failed",
          vertical: "top",
          horizontal: "right",
          severity: "error",
        });
        setIsDiscarding(false);
      }
    } catch (e) {
      setSnackbarInfo({
        message: e.message || "Discard failed",
        vertical: "top",
        horizontal: "right",
        severity: "error",
      });
      setIsDiscarding(false);
    }
  };

  return {
    mutateUpdateAssessment,
    isSubmitting,
    isDiscarding,
    snackbarInfo,
    setSnackbarInfo,
    submitAssessment,
    discardAssessment,
  };
};
